import { Button, Dialog, Icon, Loading } from '@startlibs/components';
import { getColor } from '@startlibs/utils';
import {useToggle, WithLazyResource} from '@startlibs/core'
import React, {useContext, useEffect, useRef, useState} from 'react'
import _ from 'lodash/fp'
import styled from 'styled-components';
import { EditorPageDivider } from './ReportFrontCover';
import {ImageInputContainer, ThumbnailContainer} from '../../../experts/components/ExpertProfileLayout'
import {NotProvided} from '../../ExpertAssignmentStep'
import { ReportTextButton } from './EditLayoutButton';
import {getJwt} from '../../../../hooks/useJwt'
import { jwtPostFetcher } from '../../../../utils/authFetch';
import {lazyExperts} from './lazyExperts'
import {getReportResource, isRichText} from '../../../../utils/utils'
import {IfLocale} from '../../../../hocs/IfLocale'
import { RichTextDisplay } from '../../../../components/RichTextDisplay';
import {FormattedMessage, IntlProvider} from "react-intl";
import { lazyLocale, LocaleContext } from '../../../../components/I18NProvider';
import { EditExpertReport } from '../../../experts/EditExpertReport';


const EditExpertReportDialog = styled(Dialog) `
  max-width: 70rem;
  width: 70rem;
`
const ExpertProfileBox = styled.div`
  border: 1px solid ${getColor('gray210')};
  border-radius: 5px;
  background: white;
  & ~ & {
    margin-top: 1rem;
  }
  .expertbox-heading {
    position: relative;
    display: flex;
    padding: 1.5rem 1rem;
    align-items: center;
    ${ImageInputContainer} {
      flex-basis: 4.5rem;
      flex-shrink: 0;
      width: 4.5rem;
      height: 4.5rem;
      margin: 0 1rem 0 0;
      border: 1px solid ${getColor('gray210')};
      ${ThumbnailContainer} {
        box-shadow: none;
      }
      ${Icon} {
        font-size: 24px;
      }
    }
    .expert-name {
      font-size: 14px;
      font-weight: 600;
      flex-basis: 140px;
      flex-shrink: 0;
      margin-right: 1rem;
      flex-grow: 1;
    }
    .specializations {
      flex-grow: 1;
      padding-top: .5rem;
      flex-basis: 290px;
      .specialization-wrapper {
        font-size: 12px;
      }
    }
    .expertbox-actions {
      position: absolute;
      top: .5rem;
      right: .5rem;
      display: flex;
      align-items: center;
      ${Button} {
        margin-left: .5rem;
      }
      ${Loading} {
        margin-right: .5rem;
      }
    }
  }
  .expertbox-resume {
    padding: 1.5rem;
    border-top: 1px solid ${getColor('gray210')};
    label {
      display:block;
      font-weight: 600;
      font-size: 12px;
      padding: 0;
      margin: 0 0 1rem;
      border: 0;
    }
    .resume-content {
      min-width: 200px;
    }
  }
`

export const ReportExpertsProfiles = ({caseRequest,setCaseRequest, form}) => {

  const isFirstRender = useRef(true);
  // const [experts] = useState(() => lazyExperts.use(caseRequest.reportExpertReviews).read())
  const [experts, setExperts] = useState(() => {
    const filteredReviews = caseRequest.reportExpertReviews.filter((r) => r.expertId > 0 && r.caseExpertId != null);
    const rawExperts = lazyExperts.use(filteredReviews).read();

    const reviewsMap = Object.fromEntries(
      filteredReviews.map((r) => [r.expertId, r])
    );
    
    const mergedExperts = rawExperts.map((expert) => {
      const review = reviewsMap[expert.id];

      if (review?.reportExpertEdit) {
        return {
          ...expert,
          ...review.reportExpertEdit,
          expertReviewerInfo: {
            ...expert.expertReviewerInfo,
            ...review.reportExpertEdit,
          },
          info: {
            ...expert.info,
            resume: review.reportExpertEdit.resume
          },
          reportExpertEdited: true
        };
      }

      return expert;
    });

    return mergedExperts;
  });

 useEffect(() => {
  
  if (isFirstRender.current) {
    isFirstRender.current = false;
    return; // avoid execution on initial render
  }
  const doRefresh = async () => {
    const filteredReviews = caseRequest.reportExpertReviews.filter((r) => r.expertId > 0 && r.caseExpertId != null);
    const rawExperts = await lazyExperts.use(filteredReviews).refresh().promise();

    const reviewsMap = Object.fromEntries(
      filteredReviews.map((r) => [r.expertId, r])
    );

    const mergedExperts = rawExperts.map((expert) => {
      const review = reviewsMap[expert.id];

      if (review?.reportExpertEdit) {
        return {
          ...expert,
          ...review.reportExpertEdit,
          expertReviewerInfo: {
            ...expert.expertReviewerInfo,
            ...review.reportExpertEdit,
          },
          info: {
            ...expert.info,
            resume: review.reportExpertEdit.resume
          },
          reportExpertEdited: true
        };
      } else {
        return {
          ...expert,
          expertReviewerInfo: {
            ...expert.expertReviewerInfo,
          },
        };
      }
    });
    setExperts(mergedExperts);
  };

  doRefresh();
}, [caseRequest.reportExpertReviews]);


  return <div>

    {experts.map(expert =>  <ExpertProfileItem 
      isHidden={caseRequest.reportExpertReviews.find(({expertId})=> expertId === expert.id)?.hide}
      caseExpertId={caseRequest.reportExpertReviews.find(({expertId})=> expertId === expert.id)?.caseExpertId}
      form={form} expert={expert} caseRequest={caseRequest} setCaseRequest={setCaseRequest} isAdmin/>)}

    <EditorPageDivider>
      <span>Expert profile end</span>
    </EditorPageDivider>

  </div>

}

export const ExpertProfileItem = ({expert,caseRequest,showEdit,setCaseRequest,caseExpertId, resourceOrigin,isHidden, isAdmin=false}) => {

  const {locale} = useContext(LocaleContext)
  const loading = useToggle()
  const editing = useToggle()

  const formRef = useRef();

  const handleCloseDialog = () => {
    if (formRef.current) {
      formRef.current.confirm("discard-expert-report-edit").then(() => {
        editing.close();
      }).catch(() => {
        // Cancelled, do nothing
      });
    } else {
      editing.close(); // fallback
    }
  }

  const toggleHide = () => {
    const toggled = caseRequest.reportExpertReviews.map(({expertId,reviewerType,hide,reportExpertEdit, caseExpertId}) => expertId === expert.id 
      ? {expertId,reviewerType,reportExpertEdit,caseExpertId, hide:!hide} 
      : {expertId,reviewerType,reportExpertEdit,caseExpertId, hide})
    loading.wait(jwtPostFetcher(getJwt())('/api/admin/reportExpertReviews?requestId='+caseRequest.requestId,toggled,{method:"PUT"})
      .then(() => setCaseRequest(_.set('reportExpertReviews',toggled))))
  }

  const resume = expert?.reportExpertEdit?.resume === "" ? "" : expert?.reportExpertEdit?.resume || expert?.resume || expert?.expertReviewerInfo?.resume || expert?.info?.resume 
  const specialization = expert?.reportExpertEdit?.specialization || expert?.expertReviewerInfo?.specialization || expert?.info?.specialization;

  const firstName = expert?.reportExpertEdit?.firstName || expert?.firstName;
  const lastName = expert?.reportExpertEdit?.lastName || expert?.lastName;
  const picture = expert?.reportExpertEdit?.picture || expert?.picture;
  
  return <ExpertProfileBox css={isHidden && "opacity: 0.5"} className="NODE-TO-BREAK">
    <div className="expertbox-heading">
      <ImageInputContainer>
        <ThumbnailContainer viewOnly role="img" aria-label='Expert Picture'
          css={picture && `background-image:url('${getReportResource(picture,resourceOrigin)}');`}>
          {!picture && <Icon icon="physician"/>}
        </ThumbnailContainer>
      </ImageInputContainer>
      <div className="expert-name">
        {firstName} {lastName}
      </div>
      <IfLocale not contains="LEGAL"><div className="specializations">
        <div><strong><FormattedMessage
          defaultMessage="Specialization"
          description="Specialization label in expert profile"
        />:</strong></div>
        {
          specialization.length
            ? <div className="specialization-wrapper">{specialization.map((spec,index) => <span key={spec}>{spec}{index < specialization.length - 1 && <span>, </span>}</span>)}</div>
            : <NotProvided/>
        }
      </div></IfLocale>
      {setCaseRequest && <div className="expertbox-actions">
        {loading.isOpen &&  <Loading size={16} borderWidth={3} />}
        <ReportTextButton onClick={toggleHide}>
          {isHidden ?
            <><Icon icon="view" /> Show in report</>
            : <><Icon icon="hide" /> Hide from report</>
          }
        </ReportTextButton>
        <Button 
          small
          onClick={() => {
            editing.openWith(expert)
          }}
        >
          <FormattedMessage
            defaultMessage="Edit"
            description="Report expert profile, edit button"
          />
        </Button>
        {editing.isOpen && <EditExpertReportDialog
          title={'Edit expert profile'}
          closeDialog={handleCloseDialog}
        >
          <WithLazyResource fallback={null} value={lazyLocale.use(locale)} >{([,messages]) =>
            <IntlProvider messages={messages} locale={locale} defaultLocale="en-US">
              <EditExpertReport 
                expert={expert} 
                caseRequest={caseRequest} 
                setCaseRequest={setCaseRequest} 
                caseExpertId={caseExpertId}
                isAdmin={isAdmin}
                id={expert.id} 
                closeDialog={editing.close}
                formRef={formRef}
              />    
            </IntlProvider>
          }</WithLazyResource>
          
        </EditExpertReportDialog>}
      </div>}
      {/* This opens the expert profile (logged in as an expert) */}
      {showEdit && <div className="expertbox-actions">
        <Button.Link small to="/expert/profile"><FormattedMessage
          defaultMessage="Edit profile"
          description="Report preview, edit profile button"
        /></Button.Link>
      </div>}
    </div>
    {resume && (
      <div className="expertbox-resume field-content-block">
        <label><FormattedMessage
          defaultMessage="Resume"
          description="Report preview, resume label in expert profile"
          />:</label>
        <RichTextDisplay
          className="resume-content"
          content={resume}
          useSafeNewLine={!isRichText(resume)}
        />
      </div>
    )}
  </ExpertProfileBox>

}
